import React, { useState, useEffect } from "react";
import styles from "./CSS/AudioContent.module.css";
import { Edit, Trash, Search } from "lucide-react";
import { useAudios, useDeleteAudio } from "../../../hooks/admin/useAdminAudios";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useNavigate } from "react-router-dom";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};

// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>
        <div className={styles.modalButtons}>
          <button className={styles.modalButton} onClick={onConfirm}>
            Confirm
          </button>
          <button className={styles.modalButton} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Search component
const SearchBar = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <div className={styles.searchContainer}>
      <input
        type="text"
        placeholder="Search by title..."
        value={searchTerm}
        onChange={handleSearch}
        className={styles.searchInput}
      />
      <Search className={styles.searchIcon} size={18} />
    </div>
  );
};

const AudioContent = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showModal, setShowModal] = useState(false);
  const [audioToDelete, setAudioToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAudios, setFilteredAudios] = useState([]);

  // Use React Query hooks to fetch and manage audio data
  const { audios, isLoading, error, refetchAudios } = useAudios();
  const { deleteAudio, isLoading: isDeleting } = useDeleteAudio();

  // Extract the audios array from the API response
  const audio = audios || [];

  // Update filteredAudios whenever audio or searchTerm changes
  useEffect(() => {
    if (audio) {
      const filtered = searchTerm
        ? audio.filter((item) =>
            item.title.toLowerCase().includes(searchTerm.toLowerCase())
          )
        : audio;
      
      setFilteredAudios(filtered);
      // Reset to first page when search results change
      setCurrentPage(1);
    }
  }, [audio, searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const closeModals = () => {
    setAudioToDelete(null);
  };

  // Opens the confirmation modal instead of deleting immediately.
  const confirmDelete = (id) => {
    setAudioToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion.
  const confirmDeleteAction = () => {
    if (audioToDelete !== null) {
      deleteAudio(audioToDelete, {
        onSuccess: () => {
          // Modal will be closed after successful deletion
          setShowModal(false);
          setAudioToDelete(null);
          // Data refetch is handled by the React Query mutation
        },
      });
    }
  };

  const cancelDelete = () => {
    setAudioToDelete(null);
    setShowModal(false);
  };

  // Pagination logic - adjusted to work with the filtered data
  const indexOfLastAudio = currentPage * itemsPerPage;
  const indexOfFirstAudio = indexOfLastAudio - itemsPerPage;
  const currentAudios = filteredAudios.slice(indexOfFirstAudio, indexOfLastAudio);
  const totalPages = Math.ceil(filteredAudios.length / itemsPerPage);
  
  const navigate = useNavigate();
  
  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleEdit = (id) => {
    navigate(`/admin/edit-audio/${id}`);
  };

  if (isLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div>Error loading audios: {error.message}</div>;
  }

  return (
    <div>
      <div className={styles.aboveHeader}>
        <h1>Audio Library</h1>
        <SearchBar onSearch={handleSearch} />
        <div className={styles.buttonGroup}>
          <button
            className={styles.addAudioButton}
            onClick={() => navigate("/admin/add-multiple-audio")}
          >
            Add Multiple Audio
          </button>
          <button
            className={styles.addAudioButton}
            onClick={() => navigate("/admin/add-audio")}
          >
            Add Audio
          </button>
        </div>
      </div>
      
      {filteredAudios.length === 0 ? (
        <div className={styles.noResults}>No audios found matching your search.</div>
      ) : (
        <div className={styles.audiosContainer}>
          {currentAudios.map((audio) => (
            <div key={audio.id} className={styles.audioItem}>
              <img
                src={audio.mobile_image_url || audio.computer_image_url}
                alt={audio.title}
                className={styles.audioImage}
              />
              <div className={styles.audioTitle}>
                {audio.title}
                {audio.genre_name && <span className={styles.audioGenre}>{audio.genre_name}</span>}
              </div>
              <div className={styles.iconsOverlay}>
                <EditIconComponent id={audio.id} onEdit={handleEdit} />
                <DeleteIconComponent id={audio.id} onDelete={confirmDelete} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls - only show if there are results */}
      {filteredAudios.length > 0 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={goToPrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            className={styles.paginationButton}
            onClick={goToNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this audio?"
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
        />
      )}

      {isDeleting && <div className={styles.loadingOverlay}>Deleting audio...</div>}
    </div>
  );
};

export default AudioContent;